import { v4 as uuidv4 } from "uuid";
import {
  EndpointService,
  type APIPaginatedResponse,
  type ID,
} from "@/types/api/api";
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { IAPI } from "../APIService";
import { buildQueryKey } from "@/utils/query";
import type { AxiosResponse } from "axios";
import type { CategoryVisibilityOptions } from "@/components/screens/Operations/Categories/List/Types/ListCategories";
import type { ApplyDisplayOn } from "@/components/screens/Operations/Categories/Types/CategoriesTypes";

export enum CategoryActive {
  // biome-ignore lint/style/useLiteralEnumMembers: <Want to use null as a value for logic within the codebase>
  All = null,
  Inactive = 0,
  Active = 1,
}

export enum CategoryOrderByParams {
  Name = "name",
  Active = "active",
  Order = "order",
  NumberOfItems = "number_of_items",
  Visibility = "visibility",
  HasTimeRestrictions = "has_time_restrictions",
}

export interface ICategory {
  order: number;
  visibility: number;
  theme_color: string | null;
  active: boolean;
  id: number;
  number_of_items: number;
  custom_printer_configuration: Array<unknown>;
  description: string;
  has_time_restrictions: boolean;
  created_at?: string;
  name: string;
  updated_at?: string;
}

export interface IListCategoriesParams {
  page?: number;
  active?: boolean;
  search?: string;
  order_by?: CategoryOrderByParams | string;
  page_size?: number;
  menu_id?: ID;
  configuration_id?: ID;
}

export interface ICategoryListResponse
  extends APIPaginatedResponse<ICategory> {}

export interface ICategoryListResponseAll {
  pages: ICategoryListResponse[];
  pageParams: number[];
}

export interface ICreateCategoryParams {
  name: string;
  description?: string;
  active?: boolean;
  order?: number;
  visibility?: CategoryVisibilityOptions;
}

export interface IUpdateHoursParams {
  category_id: ID;
  hours: Array<{
    id?: number;
    localID?: string;
    to_hour?: string;
    from_hour?: string;
    weekday: number;
    apply_display_on?: ApplyDisplayOn;
  }>;
}

export interface IBulkCreateCategoryRelationshipsParams {
  menu_category_id?: number | string;
  menu_item_menu_categories: Array<{
    menu_item_id: ID;
    menu_category_id: ID;
  }>;
  modifier_category_menu_items: Array<{
    modifier_category_id: ID;
    menu_item_id: ID;
  }>;
  modifier_modifier_categories: Array<{
    modifier_id: ID;
    modifier_category_id: ID;
  }>;
}

export interface IBulkCreateMenuRelationshipsResponse {
  modifier_modifier_categories: Array<{
    cost_price: number | null;
    id: number;
    is_default_option: boolean | null;
    order: number | null;
    modifier_id: number;
    value: string | null;
    modifier_category_id: number;
  }>;

  modifier_category_menu_items: Array<{
    selection_required: boolean | null;
    id: number;
    order: number | null;
    modifier_category_id: number;
    menu_item_id: number;
  }>;

  menu_item_menu_categories: Array<{
    cost_price: number | null;
    id: number;
    promotion_value: number | null;
    order: number | null;
    value: string | null;
    menu_item_id: number;
    is_promoted: boolean | null;
    menu_category_id: number;
  }>;
}

export interface ICategoryItemsRelationshipParams {
  category_id?: ID;
  menu_item_id?: ID;
}
export interface ICategoryItemsRelationship {
  menu_category: {
    visibility: number;
    has_time_restrictions: boolean;
    order: number;
    active: boolean;
    number_of_items: number;
    name: string;
    id: number;
  };
  promotion_value: number | null;
  menu_item: {
    promotion_value: number | null;
    is_promoted: boolean | null;
    order: number;
    display_description: string;
    display_image: string;
    number_of_categories: number | null;
    allow_custom_amount: boolean;
    active: boolean;
    stock_level: number;
    item_type: number;
    name: string;
    id: number;
    number_of_modifier_categories: number;
    in_stock: boolean;
    value: number;
  };
  cost_price: number;
  is_promoted: boolean | null;
  menu_category_id: number;
  order: number;
  menu_item_id: number;
  id: number;
  value: number;
}

export interface ICategoryItemsRelationshipsResponse
  extends APIPaginatedResponse<ICategoryItemsRelationship> {}

export interface ICategoryHoursParams {
  id: ID;
}

export interface ICategoryHoursResponse {
  from_hour: string;
  id: number;
  localId: string;
  to_hour: string;
  weekday: number;
  apply_display_on: ApplyDisplayOn;
}

export interface ICategoryUpdateParams {
  id: ID;
  name: string;
  description?: string;
  active?: boolean;
  order?: number;
  visibility?: CategoryVisibilityOptions;
}

export interface IDeleteItemRelationshipParams {
  relationship_id: ID;
  category_id: ID;
}

export interface IDeleteCategoryParams {
  category_id: ID;
}

export interface ICategoriesService {
  list: (
    params: IListCategoriesParams,
  ) => UseQueryResult<ICategoryListResponse, Error>;
  listAll: (
    params: IListCategoriesParams,
  ) => UseInfiniteQueryResult<ICategoryListResponseAll, Error>;
  create: () => UseMutationResult<ICategory, Error, ICreateCategoryParams>;
  retrieve: (id: ID) => UseQueryResult<ICategory, Error>;
  update: () => UseMutationResult<ICategory, Error, ICategoryUpdateParams>;
  delete: () => UseMutationResult<boolean, Error, IDeleteCategoryParams>;
  // Hours
  retrieveHours: (
    params: ICategoryHoursParams,
  ) => UseQueryResult<Array<ICategoryHoursResponse>, Error>;
  updateHours: () => UseMutationResult<
    ICategoryHoursResponse,
    Error,
    IUpdateHoursParams
  >;

  // Menu Relationships
  bulkCreateRelationships: () => UseMutationResult<
    IBulkCreateMenuRelationshipsResponse,
    Error,
    IBulkCreateCategoryRelationshipsParams
  >;

  listItemsRelationships: (
    params: ICategoryItemsRelationshipParams,
  ) => UseQueryResult<ICategoryItemsRelationshipsResponse, Error>;

  deleteItemRelationship: () => UseMutationResult<
    boolean,
    Error,
    IDeleteItemRelationshipParams
  >;
}

export class CategoriesService implements ICategoriesService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  listAll(
    params: IListCategoriesParams,
  ): UseInfiniteQueryResult<ICategoryListResponseAll, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== null),
    );
    return useInfiniteQuery({
      queryKey: buildQueryKey({
        queryKey: ["organisation", "categories", "all"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async ({ pageParam = 1 }) => {
        const { data } = await this.api.get<
          AxiosResponse<ICategoryListResponse>
        >("/menu/categories", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: { ...updatedParams, page: pageParam, page_size: 100 },
        });

        return data;
      },
      getNextPageParam: (lastPage: ICategoryListResponse) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          return Number.parseInt(url.searchParams.get("page") || "1", 10);
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      initialPageParam: 1,
    });
  }

  list(
    params: IListCategoriesParams,
  ): UseQueryResult<ICategoryListResponse, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== null && value !== undefined,
      ),
    );
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "categories"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<ICategoryListResponse>
        >("/menu/categories", {
          params: updatedParams,
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
        });

        return {
          results: data.results,
          count: data.count,
          next: data.next,
          previous: data.previous,
        };
      },
    });
  }

  create(): UseMutationResult<ICategory, Error, ICreateCategoryParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "categories", "create"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<AxiosResponse<ICategory>>(
          "/menu/me/categories/",
          {
            endpointService: EndpointService.Core,
            venueSpecific: false,
            params,
          },
        );
        return data;
      },
      onMutate: async (newAccountMenuRelationship) => {},
      onError: (err, newAccountMenuRelationship, context) => {},
      onSettled: () => {},
    });
  }

  update(): UseMutationResult<ICategory, Error, ICategoryUpdateParams> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "categories", "update"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { id, ...rest } = params;
        const { data } = await this.api.patch<AxiosResponse<ICategory>>(
          `/menu/me/categories/${id}/`,
          {
            endpointService: EndpointService.Core,
            venueSpecific: false,
            params: rest,
          },
        );
        return data;
      },
      onMutate: async (newMenu) => {},
      onError: (err, newMenu, context) => {},
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            "operations",
            "categories",
            "retrieve",
            variables.id.toString(),
          ],
        });
      },
    });
  }

  retrieve(id: ID): UseQueryResult<ICategory, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "categories", "retrieve", id],
        venueID: null,
        params: {},
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<ICategory>>(
          `/menu/categories/${id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
            params: {},
          },
        );
        return data;
      },
    });
  }

  delete(): UseMutationResult<boolean, Error, IDeleteCategoryParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "categories", "delete"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { category_id } = params;
        await this.api.delete<AxiosResponse<boolean>>(
          `/menu/me/categories/${category_id}/`,
          {
            endpointService: EndpointService.Core,
            venueSpecific: false,
          },
        );
        return true;
      },
      onMutate: async (newAccountMenuRelationship) => {},
      onError: (err, newAccountMenuRelationship, context) => {
        return false;
      },
      onSettled: () => {},
    });
  }

  // Hours

  retrieveHours(
    params: ICategoryHoursParams,
  ): UseQueryResult<Array<ICategoryHoursResponse>, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "categories", "retrieveHours", params.id],
        venueID: null,
        params: {},
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<Array<ICategoryHoursResponse>>
        >(`/menu/me/categories/${params.id}/hours/`, {
          endpointService: EndpointService.Core,
          venueSpecific: false,
        });

        // Altering the data to include a localId
        const modifiedData = data.map((hour) => ({
          ...hour,
          localId: uuidv4(),
        }));

        return modifiedData;
      },
    });
  }

  updateHours(): UseMutationResult<
    ICategoryHoursResponse,
    Error,
    IUpdateHoursParams
  > {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "categories", "updateHours"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const applicableHours = params.hours.filter(
          (hour) =>
            hour.from_hour !== undefined &&
            hour.from_hour !== "" &&
            hour.to_hour !== "" &&
            hour.to_hour !== undefined,
        );

        const { data } = await this.api.patch<
          AxiosResponse<ICategoryHoursResponse>
        >(`/menu/me/categories/${params.category_id}/hours/`, {
          endpointService: EndpointService.Core,
          venueSpecific: false,
          params: {
            hours: applicableHours,
          },
        });
        return data;
      },
      onMutate: async (newAccountMenuRelationship) => {},
      onError: (err, newAccountMenuRelationship, context) => {},
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            "operations",
            "categories",
            "retrieveHours",
            variables.category_id.toString(),
          ],
        });
      },
    });
  }

  // Menu Relationships

  bulkCreateRelationships(): UseMutationResult<
    IBulkCreateMenuRelationshipsResponse,
    Error,
    IBulkCreateCategoryRelationshipsParams
  > {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "categories", "bulkCreateRelationships"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        const { menu_category_id, ...restParams } = params;
        const { data } = await this.api.post<
          AxiosResponse<IBulkCreateMenuRelationshipsResponse>
        >("/menu/me/relationships/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: restParams,
        });
        return data;
      },
      onMutate: async (newAccountMenuRelationship) => {},
      onError: (err, newAccountMenuRelationship, context) => {},
      onSettled: (data, error, variables) => {
        if (variables.menu_category_id) {
          queryClient.invalidateQueries({
            queryKey: ["operations", "categories", "listItemsRelationships"],
          });
        }
      },
    });
  }

  listItemsRelationships(
    params: ICategoryItemsRelationshipParams,
  ): UseQueryResult<ICategoryItemsRelationshipsResponse, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["operations", "categories", "listItemsRelationships"],
        venueID: null,
        params: {},
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<ICategoryItemsRelationshipsResponse>
        >("/menu/me/menuitem_menucategories/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: {
            ...params,
            page_size: 100,
            page: 1,
          },
        });
        return data;
      },
    });
  }

  deleteItemRelationship(): UseMutationResult<
    boolean,
    Error,
    IDeleteItemRelationshipParams
  > {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["operations", "categories", "deleteItemRelationship"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params) => {
        await this.api.delete<AxiosResponse<boolean>>(
          `/menu/me/menuitem_menucategories/${params.relationship_id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return true;
      },
      onMutate: async (newAccountMenuRelationship) => {},
      onError: (err, newAccountMenuRelationship, context) => {
        return false;
      },
      onSettled: (data, error, variables) => {
        if (variables.category_id) {
          queryClient.invalidateQueries({
            queryKey: ["operations", "categories", "listItemsRelationships"],
          });
        }
      },
    });
  }
}
